import { Link, useParams, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import EventCard from "@/components/feature/EventCard";
import { categories, hotEvents } from "@/mocks/events";

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();

  const active = categories.find((c) => c.slug === slug);

  const filtered = hotEvents.filter((e) => {
    const catOk = !active || e.category === active.slug;
    const qOk = !q || e.title.includes(q) || e.summary.includes(q) || e.tags.some((t) => t.includes(q)) || e.categoryName.includes(q) || e.location.includes(q);
    return catOk && qOk;
  });

  const title = q
    ? `搜索“${q}” - 吃瓜视频在线`
    : active
    ? `${active.name} - 吃瓜视频在线`
    : "全部事件 - 吃瓜视频在线";

  const description = active
    ? `浏览${active.name}栏目：${active.tagline}，${active.desc}`
    : q
    ? `吃瓜视频在线搜索“${q}”的结果，聚合全网热点事件现场实拍与事件合集。`
    : "浏览吃瓜视频在线全部热点事件，按主题分类的现场实拍、事件合集、路人视角、对话录音与全程记录。";

  const jsonLd = active
    ? [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${active.name} - 吃瓜视频在线`,
          url: `https://hllpxyz.cn/category/${active.slug}`,
          inLanguage: "zh-CN",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: filtered.map((e, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: e.title,
              url: `https://hllpxyz.cn/event/${e.slug}`,
            })),
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://hllpxyz.cn/" },
            { "@type": "ListItem", position: 2, name: active.name, item: `https://hllpxyz.cn/category/${active.slug}` },
          ],
        },
      ]
    : [];

  return (
    <>
      <Seo title={title} description={description} keywords="吃瓜视频在线,事件合集,现场实拍,路人视角" path={`/category${active ? `/${active.slug}` : ""}`} jsonLd={jsonLd} />

      {/* 分类头部 */}
      {active ? (
        <section className="relative flex min-h-[300px] w-full items-center overflow-hidden">
          <img src={active.image} alt={`${active.name}栏目封面`} className="absolute inset-0 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/75 to-background-950/50"></div>
          <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
              <Link to="/" className="transition hover:text-primary-400">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-300">{active.name}</span>
            </nav>
            <span className="mt-5 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${active.icon} text-xl`}></i>
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">{active.name}</h1>
            <p className="mt-2 font-heading text-base text-primary-400">{active.tagline}</p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-400">{active.desc}</p>
            <div className="mt-6 flex items-center gap-6 text-sm text-foreground-300">
              <span className="flex items-center gap-1.5"><i className="ri-vidicon-line text-primary-500"></i>{active.stats}+ 事件</span>
              <span className="flex items-center gap-1.5"><i className="ri-refresh-line text-primary-500"></i>持续更新</span>
            </div>
          </div>
        </section>
      ) : (
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition hover:text-primary-500">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-300">{q ? `搜索：${q}` : "全部事件"}</span>
            </nav>
            <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              {q ? <>“{q}”的搜索结果</> : "全部热点事件"}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-500">
              {q ? `共找到 ${filtered.length} 条与“${q}”相关的现场实拍与事件合集。` : "聚合全网热点事件现场实拍，按主题分类，路人视角真实记录，全程无剪辑呈现。"}
            </p>
          </div>
        </section>
      )}

      {/* 筛选 + 网格 */}
      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/category"
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${!active && !q ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:text-foreground-950"}`}
          >
            全部
          </Link>
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${active?.slug === c.slug ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:text-foreground-950"}`}
            >
              <i className={`${c.icon} text-xs`}></i>{c.name}
            </Link>
          ))}
          <span className="ml-auto whitespace-nowrap text-sm text-foreground-500">共 {filtered.length} 条事件</span>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((e) => (
              <EventCard key={e.slug} event={e} />
            ))}
          </div>
        ) : (
          <div className="mt-12 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有找到相关事件</h2>
            <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或浏览全部热点事件合集。</p>
            <Link to="/category" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600">
              浏览全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </section>
    </>
  );
}